import usePublicCompletion from '@/hooks/usePublicCompletion';
import useIntakeDraft from '@/hooks/useIntakeDraft';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight, AlertTriangle, Stethoscope, Globe } from 'lucide-react';
import axios from 'axios';
import SignaturePad from '@/components/ui/SignaturePad';
import IntakeOutcomes from '@/components/intake/IntakeOutcomes';
import IntakePhoto from '@/components/intake/IntakePhoto';
import { intakeSizeError } from '@/utils/intakePhoto';
import { t, tOptions, tUI } from '@/utils/formTranslations';
import { validateIntakePage } from '@/utils/intakeValidation';
import { isFieldVisible } from '@/utils/formConditions';
import { prefillPatientName } from '@/utils/intakePrefill';
import { isCeremonyField } from '@/utils/intakeCeremony';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Draft persistence — localStorage key prefix. Token is appended to
// scope each intake link to its own draft, and a version suffix lets
// us invalidate old draft shapes if this code changes incompatibly.
// 14-day TTL matches the typical max time between a front-desk link
// send and the patient actually showing up; older drafts auto-expire.
const DRAFT_PREFIX = 'cervica_intake_draft_v1_';
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

function loadDraft(token, patientIds = []) {
  if (!token) return null;
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + token);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        if (parsed.savedAt && Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
          localStorage.removeItem(DRAFT_PREFIX + token);
        } else {
          return parsed;
        }
      }
    }
  } catch { /* fall through to the same-patient scan */ }

  // No draft under THIS token. The front desk resends links freely and
  // every resend is a NEW token, so a patient who half-filled link #3
  // opens link #4 to blank forms while their answers sit one key over
  // (Larry Banks 2026-07-31: six links, zero submissions). Response
  // keys are patientId_templateId — patient-scoped, token-agnostic —
  // so the freshest non-expired draft for the SAME patient is fully
  // compatible. Adopt it; the auto-save effect re-homes it under the
  // current token on the next keystroke.
  if (!patientIds.length) return null;
  try {
    let best = null;
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(DRAFT_PREFIX)) continue;
      let parsed;
      try { parsed = JSON.parse(localStorage.getItem(k)); } catch { continue; }
      if (!parsed || typeof parsed !== 'object' || !parsed.savedAt) continue;
      if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) continue;
      const rKeys = Object.keys(parsed.allResponses || {});
      if (!rKeys.some((rk) => patientIds.some((pid) => rk.startsWith(`${pid}_`)))) continue;
      if (!best || parsed.savedAt > best.savedAt) best = parsed;
    }
    return best;
  } catch {
    return null;
  }
}

function saveDraft(token, payload) {
  if (!token) return;
  try {
    const value = JSON.stringify({ ...payload, savedAt: Date.now() });
    localStorage.setItem(DRAFT_PREFIX + token, value);
    return localStorage.getItem(DRAFT_PREFIX + token) === value;
  } catch {
    return false; // Surface this separately from the server checkpoint status.
  }
}

function clearDraft(token) {
  if (!token) return;
  try { localStorage.removeItem(DRAFT_PREFIX + token); } catch {}
}

export default function IntakePage() {
  const { token } = useParams();
  // A new magic link must never inherit another recipient's data or signature.
  return <IntakeSession key={token} token={token} />;
}

function IntakeSession({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Distinguish load-time vs submit-time errors so the header text on
  // the error screen matches what actually happened. Old screen said
  // "Unable to Load Forms" for submit failures too, which made every
  // patient + front-desk debug start in the wrong place.
  const [errorKind, setErrorKind] = useState('load');
  const [data, setData] = useState(null);
  const [currentForm, setCurrentForm] = useState(0);
  const [preparingPhotos, setPreparingPhotos] = useState(0);
  const photoBusy = busy => setPreparingPhotos(n => Math.max(0, n + (busy ? 1 : -1)));
  const [currentPatientIdx, setCurrentPatientIdx] = useState(0);
  const [allResponses, setAllResponses] = useState({}); // { patientId_templateId: { fieldId: value } }
  const [signatures, setSignatures] = useState({}); // { patientId_templateId: { data, signer_name } }
  const [submitting, setSubmitting] = useState(false);
  const intakeSubmitLock = useRef(false);
  const cloud=useIntakeDraft(`${API_BASE}/forms/public/intake/${token}`);
  const [draftConflict,setDraftConflict]=useState(null);
  const [recoveryNotice,setRecoveryNotice]=useState('');
  const [cloudReady,setCloudReady]=useState(false);
  const [localSaveFailed,setLocalSaveFailed]=useState(false);
  const lastCloudSave=useRef(Date.now());
  const completion=usePublicCompletion({persistOperation:cloud.prepare,lookupReceipt:cloud.lookupReceipt,storageKey:'cervica_intake_operation_v1_'+token,recoveryPath:`${API_BASE}/forms/public/intake/${token}/submit`,onRecovered:()=>{clearDraft(token);setSubmitted(true)},validateReceipt:receipt=>Array.isArray(receipt.submissions)&&receipt.submissions.length>0&&receipt.submissions.every(row=>row.id&&row.patient_id&&row.form_template_id)});
  const [submitted, setSubmitted] = useState(false);
  const [sharedData, setSharedData] = useState({});
  const [lang, setLang] = useState('en'); // 'en' or 'es'
  // Force an explicit language choice before any form renders — a Spanish
  // speaker who can't read the English UI can't hunt for a tiny toggle.
  const [languageChosen, setLanguageChosen] = useState(false);
  // Identity gate — intake links go to SHARED family phones (Juan Segura
  // 2026-07-09: a male chart gained a C-section from a family member's
  // answers). Must confirm who the forms are FOR before anything renders.
  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  const [notMe, setNotMe] = useState(false);
  // Family hand-off gate: each family member's forms re-confirm identity
  // when the session advances to them (integrity audit follow-up,
  // 2026-07-09) — the last drift point for shared-phone sessions.
  const [confirmedPatientIds, setConfirmedPatientIds] = useState([]);
  const [draftRestored, setDraftRestored] = useState(false);
  // Validation banner — only shows after the patient has tried to
  // advance, never on initial render. Declared up here with the other
  // useStates so the hook order stays stable across the loading /
  // loaded / submitted branches (the variant of React #310 reported
  // when the patient hit this on 5G — first render bailed via the
  // "no templates yet" early return, then the post-load render
  // declared these hooks fresh and React saw a hook-count mismatch).
  const [validation, setValidation] = useState({ ok: true, missingLabels: [], missingFieldIds: [], signatureMissing: false });
  const [attemptedAdvance, setAttemptedAdvance] = useState(false);
  const sigPadRef = useRef(null);
  // Review & Sign — one name + one drawn signature applied to every
  // acknowledged form instead of a full signing ceremony per form.
  // Each signature-required form keeps its own explicit "I have read
  // and agree" checkbox and its own signature record (Daniel's
  // condition 2026-07-31), so per-document intent is preserved.
  const [reviewing, setReviewing] = useState(false);
  const [acks, setAcks] = useState({}); // rKey → true
  const [reviewName, setReviewName] = useState('');
  const [relByPatient, setRelByPatient] = useState({}); // patientId → signer_relationship

  // Per-form pads are gone — the only pad mounts on the Review & Sign
  // step, so mid-form navigation has nothing to capture.
  function captureCurrentSignature() {}

  useEffect(() => {
    loadForms();
  }, [token]);

  async function loadForms() {
    setLoading(true); setError(''); setCloudReady(false);
    try {
      const res = await axios.get(`${API_BASE}/forms/public/intake/${token}`, {timeout:20000});
      setData(res.data);
      // Initialize empty responses with the SAME key shape submit reads.
      // Before this normalization, `initial` keyed by templateId only
      // ({ tplId: {} }) while writes used `${patientId}_${templateId}`
      // and submit read the same patient-prefixed key. The two coexisted
      // harmlessly until a draft restore landed responses under stale
      // keys — then submit would read the patient-prefixed key, find
      // the empty entry from `initial`, and send {} for the form even
      // though the data was sitting one key over. Anna's 2026-06-16
      // report of patients losing 90min of intake work pointed here.
      const patientList = res.data?.patients || (res.data?.patient ? [res.data.patient] : []);
      const initial = {};
      for (const pt of patientList) {
        for (const t of (res.data.templates || [])) {
          // Seed LOCKED identity fields with the chart values so (a)
          // required-field validation passes — the locked read-only
          // boxes can never be typed into, and a new patient was
          // blocked at 'missing name/DOB' she could see but not edit
          // (Damaris 2026-07-17) — and (b) the stored submission
          // carries the true legal identity.
          const seeded = {};
          const tFields = typeof t.fields === 'string' ? (() => { try { return JSON.parse(t.fields); } catch { return []; } })() : (t.fields || []);
          for (const f of tFields) {
            if (LOCKED_IDENTITY_FIELDS.has(f.id)) {
              const v = identityValueFor(f.id, pt);
              if (v) seeded[f.id] = v;
            }
          }
          initial[`${pt.id}_${t.id}`] = seeded;
        }
      }
      // Keep the legacy templateId-only keys too, in case a saved
      // draft from before this fix is restored — the spread merge below
      // still picks them up. New writes always go to the patient-
      // prefixed shape via rKey().
      for (const t of (res.data.templates || [])) {
        if (!(t.id in initial)) initial[t.id] = {};
      }

      // Restore a previously-saved draft if one exists for this token.
      // The pre-fix behavior was to wipe everything on re-mount — which
      // happens routinely on mobile Safari when the tab is backgrounded
      // long enough to be evicted from memory. Patients were getting to
      // the signature page, putting the phone down for a minute, and
      // returning to find the form blank.
      let draft = loadDraft(token, patientList.map((p) => p.id));
      const recovery=await cloud.load();
      const allowedKeys=new Set(patientList.flatMap(pt=>(res.data.templates||[]).filter(t=>!res.data.template_ids_by_patient?.[pt.id]||res.data.template_ids_by_patient[pt.id].includes(t.id)).map(t=>`${pt.id}_${t.id}`)));
      if(draft){draft={...draft,allResponses:Object.fromEntries(Object.entries(draft.allResponses||{}).filter(([k])=>allowedKeys.has(k))),signatures:{},currentForm:0,currentPatientIdx:0};}
      if(recovery.status==='completed'){clearDraft(token);completion.restore(null);setSubmitted(true);}
      else if(recovery.operation){completion.restore({...recovery.operation,path:`${API_BASE}/forms/public/intake/${token}/submit`});}
      else if(recovery.draft){
        if(draft&&JSON.stringify(draft.allResponses)!==JSON.stringify(recovery.draft.allResponses))setDraftConflict(recovery.draft);
        else draft=recovery.draft;
      }
      setCloudReady(true);

      if (draft && draft.allResponses && typeof draft.allResponses === 'object') {
        // Merge over the empty initial so any templates added/changed
        // server-side since the draft was saved still appear as empty
        // entries the patient can fill rather than disappearing.
        // Draft restore must not wipe the identity seeds — a draft key
        // saved before the seeding existed replaces the whole per-form
        // object, so merge the seeds back into each restored entry.
        const merged = { ...initial, ...draft.allResponses };
        for (const k of Object.keys(initial)) {
          for (const [fid, v] of Object.entries(initial[k] || {})) {
            if (merged[k] && (merged[k][fid] == null || merged[k][fid] === '')) merged[k][fid] = v;
          }
        }
        setAllResponses(merged);
        if (draft.signatures && typeof draft.signatures === 'object') {
          setSignatures(draft.signatures);
        }
        if (typeof draft.currentForm === 'number') setCurrentForm(draft.currentForm);
        if (typeof draft.currentPatientIdx === 'number') setCurrentPatientIdx(draft.currentPatientIdx);
        if (draft.lang === 'en' || draft.lang === 'es') { setLang(draft.lang); setLanguageChosen(true); }
        setDraftRestored(true);
      } else {
        setAllResponses(initial);
      }
    } catch (err) {
      setErrorKind('load');
      setError(err.response?.data?.error || 'We could not load your forms or check saved progress. Your saved answers have not been replaced. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  // Auto-save the in-progress draft to localStorage whenever any of the
  // form state changes. Cheap: localStorage writes are sync but local;
  // we're saving a few KB at most. Skipped until after the initial load
  // has populated state to avoid stomping a freshly-restored draft with
  // the empty initialState.
  useEffect(() => {
    // Never replace an existing draft until both forms and recovery hydrate.
    // Also preserve the device copy while the patient chooses between versions.
    if (loading || !cloudReady || error || draftConflict || completion.pending || !token || submitted) return;
    setLocalSaveFailed(!saveDraft(token, {
      allResponses,
      signatures,
      currentForm,
      currentPatientIdx,
      lang,
    }));
  }, [allResponses, signatures, currentForm, currentPatientIdx, lang, loading, token, submitted, cloudReady, error, draftConflict, completion.pending]);

  useEffect(()=>{
    if(!cloudReady||loading||submitted||completion.pending||draftConflict||!identityConfirmed||!data?.templates?.length)return;
    cloud.markDirty();
    const timer=setTimeout(()=>{lastCloudSave.current=Date.now();cloud.save({allResponses,lang}).catch(()=>{});},Math.max(700,5000-(Date.now()-lastCloudSave.current)));
    return ()=>clearTimeout(timer);
  },[allResponses,lang,cloudReady,loading,submitted,completion.pending,draftConflict,identityConfirmed]);
  useEffect(()=>{
    if(submitted||(!cloud.hasUnsavedChanges&&!completion.pending))return;
    const warn=event=>{event.preventDefault();event.returnValue='';};
    window.addEventListener('beforeunload',warn);
    return()=>window.removeEventListener('beforeunload',warn);
  },[submitted,cloud.hasUnsavedChanges,completion.pending]);
  useEffect(()=>{window.__cervicaPublicIntakeOpen=!submitted;return()=>{delete window.__cervicaPublicIntakeOpen;}},[submitted]);


  const patients = data?.patients || (data?.patient ? [data.patient] : []);
  const currentPatient = patients[currentPatientIdx];
  const isMultiPatient = patients.length > 1;

  // Per-patient template routing (family links): the payload carries
  // template_ids_by_patient so each family member gets their own
  // age-appropriate bundle (kids → pediatric HHQ + minor consent,
  // adults → adult HHQ). Falls back to the shared list.
  function templatesFor(pt) {
    const all = data?.templates || [];
    const byPt = data?.template_ids_by_patient;
    if (!byPt || !pt || !Array.isArray(byPt[pt.id])) return all;
    const wanted = new Set(byPt[pt.id]);
    return all.filter((t) => wanted.has(t.id));
  }

  // Response key includes patient ID for multi-patient support
  function rKey(templateId) {
    return currentPatient ? `${currentPatient.id}_${templateId}` : templateId;
  }

  useEffect(()=>{if(completion.error&&!completion.pending)alert(completion.error)},[completion.error,completion.pending]);

  async function handleSubmit(reviewSignatures = null) {
    if(draftConflict||!cloudReady||preparingPhotos)return;
    if(intakeSubmitLock.current)return;intakeSubmitLock.current=true;
    setSubmitting(true);
    try {
      // The Review & Sign step hands in the applied-signature map; the
      // per-form pads are gone.
      const finalSignatures = reviewSignatures || { ...signatures };
      const ackStamp = new Date().toISOString();

      // Build submissions for ALL patients — each against their OWN
      // template list (family links age-route the HHQ per patient).
      const submissions = [];
      for (const pt of patients) {
        for (const t of templatesFor(pt)) {
          const key = `${pt.id}_${t.id}`;
          // Prefilled name goes UNDER whatever the patient typed, so an
          // untouched box still submits the value they can see on screen
          // and an edited one keeps their correction. Same merge order as
          // the render path below — if these two ever diverge, the patient
          // signs one thing and we store another.
          const tFields = typeof t.fields === 'string' ? JSON.parse(t.fields) : (t.fields || []);
          submissions.push({
            template_id: t.id,
            patient_id: pt.id,
            responses: {
              ...prefillPatientName(tFields, pt),
              ...(allResponses[key] || {}),
              // The Review & Sign checkbox is a distinct acknowledgment
              // per document — record it on the submission itself.
              ...(t.requires_signature && finalSignatures[key]?.data
                ? { _esign_acknowledged_at: ackStamp } : {}),
            },
            signature: finalSignatures[key] || null,
          });
        }
      }

      // Pre-submit forensic snapshot. The next time a patient hits the
      // empty-bundle path we want a record of what the client THINKS it
      // is sending. Without this we can't tell whether (a) the client
      // sent populated bodies that the server lost, or (b) the client
      // built the body from empty state. Logged AND attached to the
      // POST as a debug header so the server access log captures it
      // even if the page is closed before the response arrives.
      const _audit = {
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
        patient_count: patients.length,
        template_count: (data?.templates || []).length,
        all_responses_key_count: Object.keys(allResponses).length,
        all_responses_key_sample: Object.keys(allResponses).slice(0, 20),
        submission_keys_attempted: submissions.map((s) => `${s.patient_id}_${s.template_id}`),
        per_submission_filled: submissions.map((s) => Object.keys(s.responses || {}).length),
      };
      const sizeError = intakeSizeError({allResponses, lang}, {submissions, _client_audit:_audit});
      if (sizeError) { alert(sizeError); return; }
      await completion.run(`${API_BASE}/forms/public/intake/${token}/submit`,{submissions,_client_audit:_audit},()=>{clearDraft(token);setSubmitted(true)});

    } catch (err) {
      // Keep the editable answers mounted when request construction fails.
      setRecoveryNotice('Your forms were not submitted. Your answers are still on this page. Please try again or contact the practice.');

    } finally {
      intakeSubmitLock.current=false;
      setSubmitting(false);
    }
  }

  async function checkSaved(){
    try{setRecoveryNotice('');const r=await cloud.load();if(r.status==='completed'){completion.restore(null);clearDraft(token);setSubmitted(true);}
      else if(r.operation)completion.restore({...r.operation,path:`${API_BASE}/forms/public/intake/${token}/submit`});
      else if(r.draft)setDraftConflict(r.draft);
    }catch(e){setRecoveryNotice(e.response?.data?.error||'Cannot check saved status. Keep this page open.');}
  }
  const saveNotice=<div className="sticky top-0 z-20 border-b bg-white px-4 py-3 text-sm" role="status" aria-live="polite"><span>{recoveryNotice||cloud.status}</span>{localSaveFailed&&<p className="mt-1 text-amber-800">This device cannot keep a recovery copy. Keep this page open until your progress is saved to the practice.</p>}<button type="button" onClick={checkSaved} className="ml-4 underline">Check saved status</button><p className="mt-1 text-xs text-slate-600">Progress is a draft until submission is confirmed. Resume with this same link; contact the practice if it expires.</p></div>;
  if(completion.pending)return <div className="mx-auto mt-12 max-w-lg rounded-xl border bg-white p-6">{saveNotice}<p role="alert">{completion.error||'Your earlier submission needs confirmation.'}</p><button disabled={completion.working} onClick={completion.retry} className="mt-4 rounded bg-teal-700 px-4 py-2 text-white">{completion.working?'Confirming…':'Retry same submission'}</button></div>;
  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen kind={errorKind} message={error} onRetry={loadForms} />;
  if (submitted) return <SuccessScreen practiceName={data?.practice_name} />;
  if (!data) return <ErrorScreen message="No intake data available." />;

  // Language gate — shown once, before any form. Both options are always
  // visible so a Spanish speaker recognizes "Español" without reading English.
  if (!languageChosen) {
    return <LanguageGate practiceName={data.practice_name} onPick={(l) => { setLang(l); setLanguageChosen(true); }} />;
  }

  if(draftConflict)return <div className="mx-auto max-w-xl p-6"><h1 className="text-xl font-semibold">Two versions of your answers are available</h1><p className="my-4">This device has answers that differ from the saved draft. Keep this page open until you choose which to continue. Neither version has been submitted.</p><button className="mr-4 rounded border p-3" onClick={()=>{setAllResponses(draftConflict.allResponses);setCurrentForm(0);setCurrentPatientIdx(0);setDraftConflict(null);}}>Continue saved draft</button><button className="rounded border p-3" onClick={()=>setDraftConflict(null)}>Continue this device’s answers</button></div>;

  // Outcomes-only token: skip the template/signature flow entirely and
  // render the patient-facing PROM questionnaires. Phase F2.
  if ((!data.templates || data.templates.length === 0) && data.outcomes?.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">{saveNotice}
        <div className="bg-[#0f3d3a] px-4 py-4 sm:px-6">
          <div className="flex items-center justify-end gap-1">
            <Globe className="h-3.5 w-3.5 text-teal-300" />
            <button onClick={() => setLang('en')} className={`rounded px-2 py-0.5 text-[11px] font-medium ${lang === 'en' ? 'bg-white text-teal-800' : 'text-teal-200 hover:text-white'}`}>English</button>
            <button onClick={() => setLang('es')} className={`rounded px-2 py-0.5 text-[11px] font-medium ${lang === 'es' ? 'bg-white text-teal-800' : 'text-teal-200 hover:text-white'}`}>Español</button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Stethoscope className="h-5 w-5 text-teal-300" />
            <h1 className="text-[18px] font-bold text-white">{data.practice_name}</h1>
          </div>
        </div>
        <IntakeOutcomes
          token={token}
          patient={data.patient}
          outcomes={data.outcomes}
          lang={lang}
          onComplete={() => setSubmitted(true)}
        />
      </div>
    );
  }

  if (!data.templates?.length) return <ErrorScreen message="No forms available." />;

  const templates = templatesFor(currentPatient);
  const template = templates[currentForm];
  const fields = typeof template.fields === 'string' ? JSON.parse(template.fields) : (template.fields || []);
  // Veronica Cardona 2026-07-28: patients retype their own name on every
  // signature page. The fields stay (a printed name beside a signature is
  // what makes the lien and the assignment enforceable) but the value is
  // filled in from what they already entered, and stays editable. Prefill
  // sits UNDER the answers so anything typed wins — the same merge order
  // the submit path uses.
  const responses = {
    ...prefillPatientName(fields, currentPatient),
    ...(allResponses[rKey(template.id)] || {}),
  };
  const isLastForm = currentForm === templates.length - 1;
  const isLastPatient = currentPatientIdx === patients.length - 1;
  const isLast = isLastForm && isLastPatient;

  function updateField(fieldId, value) {
    setAllResponses({
      ...allResponses,
      [rKey(template.id)]: { ...responses, [fieldId]: value },
    });
  }

  function validateCurrentPage() {
    // Thin wrapper around the pure validator in utils/intakeValidation.js.
    // Ceremony fields (per-form printed name / typed signature / witness
    // blocks) no longer render, so they must not be validated either.
    // The signature itself is enforced on the Review & Sign step, so
    // page-level validation always treats it as satisfied.
    const tFields = (() => {
      let f = template?.fields;
      if (typeof f === 'string') { try { f = JSON.parse(f); } catch { f = []; } }
      return Array.isArray(f) ? f.filter((x) => !isCeremonyField(x, template)) : [];
    })();
    return validateIntakePage({
      template: template ? { ...template, fields: tFields } : template,
      responses,
      hasSignature: true,
      patientGender: currentPatient?.gender,
    });
  }

  // (validation + attemptedAdvance hoisted to the top of the component
  // so the hook order stays stable across the loading / loaded /
  // submitted branches — see the original declarations above.)

  // Scroll to the first missing field when validation fires. Tries the
  // field's input by id, then falls back to the field wrapper.
  function scrollToFirstMissing(missingFieldIds) {
    if (!missingFieldIds.length) return;
    const firstId = missingFieldIds[0];
    setTimeout(() => {
      const el = document.querySelector(`[data-field-id="${firstId}"]`) || document.getElementById(`field-${firstId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el.querySelector('input, textarea, select');
        focusable?.focus?.();
      }
    }, 50);
  }

  function handleAttemptAdvance(actionFn) {
    const result = validateCurrentPage();
    setValidation(result);
    setAttemptedAdvance(true);

    // Diagnostic logging — fires whenever validation blocks. Captures
    // EVERY required field's current value so we can see exactly what
    // staff was looking at when they reported "all boxes filled but
    // won't submit." Console only (no PHI sent anywhere).
    if (!result.ok) {
      const required = fields.filter((f) => f.required && f.type !== 'section' && f.type !== 'static_text');
      const audit = required.map((f) => {
        const val = responses[f.id];
        let display;
        if (val == null) display = '∅ (never set)';
        else if (val === '') display = '"" (empty string)';
        else if (typeof val === 'object') display = `object: ${JSON.stringify(val).slice(0, 80)}`;
        else display = `${typeof val}: ${String(val).slice(0, 60)}`;
        return {
          id: f.id,
          label: f.label,
          type: f.type,
          required: f.required,
          value: display,
          isMissing: result.missingFieldIds.includes(f.id),
        };
      });
      const sigDiag = template?.requires_signature
        ? {
            requiresSignature: true,
            signatureInState: !!signatures[rKey(template.id)]?.data,
            signaturePadHasData: sigPadRef.current ? !!sigPadRef.current.getData?.() : false,
            signatureKey: rKey(template.id),
          }
        : { requiresSignature: false };
      // eslint-disable-next-line no-console
      console.warn('[IntakePage] submit blocked — diagnostic dump', {
        templateId: template?.id,
        templateName: template?.name,
        patientId: currentPatient?.id,
        currentForm,
        missingLabels: result.missingLabels,
        signatureDiag: sigDiag,
        requiredFields: audit,
      });
    }

    if (result.ok) {
      setAttemptedAdvance(false);
      actionFn();
    } else {
      scrollToFirstMissing(result.missingFieldIds);
    }
  }

  if (!identityConfirmed) {
    const gateNames = (data.patients && data.patients.length > 1)
      ? data.patients.map((p) => `${p.first_name} ${p.last_name}`).join(lang === 'es' ? ' y ' : ' and ')
      : `${data.patient?.first_name || ''} ${data.patient?.last_name || ''}`.trim();
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <Stethoscope className="mx-auto mb-2 h-8 w-8 text-teal-600" />
          <h1 className="text-[17px] font-bold text-gray-900">{data.practice_name}</h1>
          {notMe ? (
            <>
              <p className="mt-4 text-[14px] font-semibold text-gray-800">
                {lang === 'es' ? 'Estos formularios no son para usted.' : 'These forms are not for you.'}
              </p>
              <p className="mt-2 text-[13px] text-gray-600">
                {lang === 'es'
                  ? 'Por favor no continúe. Entregue el teléfono a la persona correcta o llame a la oficina para recibir su propio enlace.'
                  : 'Please do not continue. Hand the phone to the right person, or call the office to get your own link.'}
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-[13px] text-gray-500">
                {lang === 'es' ? 'Estos formularios son para:' : 'These forms are for:'}
              </p>
              <p className="mt-1 text-[20px] font-bold text-teal-700">{gateNames}</p>
              <p className="mt-3 text-[12px] text-gray-500">
                {lang === 'es'
                  ? 'Las respuestas pasan directamente al expediente médico de esta persona. Si usted no es esta persona (o su padre/tutor), no continúe.'
                  : "The answers go directly into this person's medical chart. If you are not this person (or their parent/guardian), do not continue."}
              </p>
              <button
                type="button"
                onClick={() => { setIdentityConfirmed(true); if (currentPatient?.id) setConfirmedPatientIds([currentPatient.id]); }}
                className="mt-5 w-full rounded-xl bg-teal-600 px-4 py-3 text-[15px] font-bold text-white hover:bg-teal-700"
              >
                {lang === 'es' ? 'Sí, soy esta persona (o su tutor) — continuar' : 'Yes, this is me (or I am their guardian) — continue'}
              </button>
              <button
                type="button"
                onClick={() => setNotMe(true)}
                className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-gray-600 hover:bg-gray-50"
              >
                {lang === 'es' ? 'No soy esta persona' : 'This is not me'}
              </button>
            </>
          )}
          <div className="mt-4 flex items-center justify-center gap-2">
            <button onClick={() => setLang('en')} className={`rounded px-2 py-0.5 text-[11px] font-medium ${lang === 'en' ? 'bg-teal-100 text-teal-800' : 'text-gray-400'}`}>English</button>
            <button onClick={() => setLang('es')} className={`rounded px-2 py-0.5 text-[11px] font-medium ${lang === 'es' ? 'bg-teal-100 text-teal-800' : 'text-gray-400'}`}>Español</button>
          </div>
        </div>
      </div>
    );
  }

  if (isMultiPatient && currentPatient && !confirmedPatientIds.includes(currentPatient.id)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-2xl border border-teal-200 bg-white p-6 text-center shadow-sm">
          <Stethoscope className="mx-auto mb-2 h-8 w-8 text-teal-600" />
          <p className="text-[13px] text-gray-500">
            {lang === 'es' ? 'Ahora completando formularios para:' : 'Now filling forms for:'}
          </p>
          <p className="mt-1 text-[22px] font-bold text-teal-700">{currentPatient.first_name} {currentPatient.last_name}</p>
          <p className="mt-3 text-[12px] text-gray-500">
            {lang === 'es'
              ? `Por favor entregue el teléfono a ${currentPatient.first_name} o a su padre/tutor. Las respuestas pasan directamente a SU expediente.`
              : `Please hand the phone to ${currentPatient.first_name} or their parent/guardian. The answers go directly into THEIR chart.`}
          </p>
          <button
            type="button"
            onClick={() => setConfirmedPatientIds((ids) => [...ids, currentPatient.id])}
            className="mt-5 w-full rounded-xl bg-teal-600 px-4 py-3 text-[15px] font-bold text-white hover:bg-teal-700"
          >
            {lang === 'es' ? `Listo — continuar con los formularios de ${currentPatient.first_name}` : `Ready — continue with ${currentPatient.first_name}'s forms`}
          </button>
        </div>
      </div>
    );
  }

  // ===== Review & Sign — one name, one signature, one explicit
  // acknowledgment per document. The drawn signature is applied to
  // every acknowledged form's own signature record.
  if (reviewing) {
    const sigItems = [];
    for (const pt of patients) {
      for (const t of templatesFor(pt)) {
        if (t.requires_signature) sigItems.push({ pt, t, key: `${pt.id}_${t.id}` });
      }
    }
    const allAcked = sigItems.every((it) => acks[it.key]);
    const canSubmit = allAcked && reviewName.trim().length >= 2 && !submitting;
    const patientsWithSigs = [...new Map(sigItems.map((it) => [it.pt.id, it.pt])).values()];

    function submitFromReview() {
      const padData = sigPadRef.current?.getData?.();
      if (!padData) {
        alert(lang === 'es' ? 'Por favor firme en el recuadro.' : 'Please sign in the signature box.');
        return;
      }
      const final = { ...signatures };
      for (const it of sigItems) {
        final[it.key] = {
          data: padData,
          signer_name: reviewName.trim(),
          signer_relationship: relByPatient[it.pt.id] || (patientIsMinor(it.pt) ? 'parent' : 'self'),
        };
      }
      handleSubmit(final);
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">{saveNotice}
        <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
          <h1 className="text-[20px] font-bold text-gray-900">
            {lang === 'es' ? 'Revisar y firmar' : 'Review & Sign'}
          </h1>
          <p className="mt-1 text-[13px] text-gray-600">
            {lang === 'es'
              ? 'Marque cada documento para confirmar que lo leyó y está de acuerdo. Su firma se aplicará a cada documento marcado.'
              : 'Check each document to confirm you have read and agree to it. Your signature below will be applied to each checked document.'}
          </p>

          <div className="mt-4 space-y-2">
            {sigItems.map(({ pt, t, key }) => (
              <label key={key} className={`flex items-start gap-3 rounded-xl border-2 p-3.5 transition-colors ${
                acks[key] ? 'border-teal-300 bg-teal-50/60' : 'border-gray-200 bg-white'
              }`}>
                <input
                  type="checkbox"
                  checked={!!acks[key]}
                  onChange={(e) => setAcks({ ...acks, [key]: e.target.checked })}
                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="flex-1">
                  <span className="block text-[14px] font-semibold text-gray-800">{t.name}</span>
                  <span className="block text-[12px] text-gray-500">
                    {isMultiPatient ? `${pt.first_name} ${pt.last_name} — ` : ''}
                    {lang === 'es' ? 'He leído y estoy de acuerdo con este documento.' : 'I have read and agree to this document.'}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {patientsWithSigs.map((pt) => {
            const isMinor = patientIsMinor(pt);
            const rel = relByPatient[pt.id] || (isMinor ? 'parent' : 'self');
            const options = SIGNER_RELATIONSHIPS.filter((o) => !(isMinor && o.value === 'self'));
            // A single adult signing for themselves needs no relationship
            // question at all.
            if (!isMultiPatient && !isMinor) return null;
            return (
              <div key={pt.id} className="mt-3">
                <label className="mb-1 block text-[13px] font-semibold text-gray-700">
                  {lang === 'es' ? `¿Quién firma por ${pt.first_name}?` : `Who is signing for ${pt.first_name}?`}
                </label>
                <select
                  value={rel}
                  onChange={(e) => setRelByPatient({ ...relByPatient, [pt.id]: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none"
                >
                  {options.map((o) => <option key={o.value} value={o.value}>{lang === 'es' ? o.es : o.en}</option>)}
                </select>
              </div>
            );
          })}

          <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-900/[0.04]">
            <SignaturePad
              ref={sigPadRef}
              signerName={reviewName}
              onNameChange={setReviewName}
              onSign={() => {}}
            />
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={() => setReviewing(false)}
              className="flex items-center gap-1.5 rounded-xl px-5 py-3 text-[14px] font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              <ChevronLeft className="h-4 w-4" /> {lang === 'es' ? 'Volver' : 'Back to forms'}
            </button>
            <button
              onClick={submitFromReview}
              disabled={!canSubmit}
              className="flex items-center gap-2 rounded-xl bg-teal-600 px-8 py-3 text-[14px] font-bold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-40"
            >
              {submitting
                ? (lang === 'es' ? 'Enviando...' : 'Submitting...')
                : (lang === 'es' ? 'Firmar y enviar todo' : `Sign & Submit All${isMultiPatient ? ` (${patients.length} patients)` : ''}`)}
              <CheckCircle className="h-4 w-4" />
            </button>
          </div>
          {!allAcked && (
            <p className="mt-2 text-center text-[12px] text-gray-500">
              {lang === 'es' ? 'Marque todos los documentos para continuar.' : 'Check every document above to enable submitting.'}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">{saveNotice}
      {/* Header */}
      <div className="bg-[#0f3d3a] px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div />
          {/* Language selector */}
          <div className="flex items-center gap-1">
            <Globe className="h-3.5 w-3.5 text-teal-300" />
            <button onClick={() => setLang('en')} className={`rounded px-2 py-0.5 text-[11px] font-medium ${lang === 'en' ? 'bg-white text-teal-800' : 'text-teal-200 hover:text-white'}`}>English</button>
            <button onClick={() => setLang('es')} className={`rounded px-2 py-0.5 text-[11px] font-medium ${lang === 'es' ? 'bg-white text-teal-800' : 'text-teal-200 hover:text-white'}`}>Español</button>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-2">
          <Stethoscope className="h-5 w-5 text-teal-300" />
          <h1 className="text-[18px] font-bold text-white">{data.practice_name}</h1>
        </div>
        <p className="mt-1 text-[13px] text-teal-200 text-center">
          {lang === 'es' ? `Bienvenido/a, ${data.patient?.first_name}! Por favor complete los siguientes formularios.` : `Welcome, ${data.patient?.first_name}! Please complete the following forms.`}
        </p>
      </div>

      {/* Progress */}
      <div className="mx-auto max-w-2xl px-4 pt-4 sm:px-6">
        <div className="flex items-center gap-2 mb-1">
          {templates.map((t, i) => (
            <div key={t.id} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${
                i < currentForm ? 'bg-teal-500' : i === currentForm ? 'bg-teal-400' : 'bg-gray-200'
              }`} />
            </div>
          ))}
        </div>
        <p className="text-[12px] text-gray-500 text-center">
          {isMultiPatient && <span className="text-teal-600 font-semibold">Forms for {currentPatient?.first_name} {currentPatient?.last_name} — </span>}
          Form {currentForm + 1} of {templates.length}: <strong>{template.name}</strong>
        </p>
        {/* Auto-save indicator — small green chip near the progress bar so
            the patient (and staff) can see that progress is being saved.
            Chris Davis flagged 2026-06-05 worry about losing work on crash. */}
        <div className="mt-1 flex items-center justify-center gap-1">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
          <p className="text-[10px] text-emerald-700">
            {lang === 'es' ? 'Guardando automáticamente' : 'Auto-saving as you go'}
          </p>
        </div>
        {draftRestored && (
          <div className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-teal-600 flex-shrink-0" />
            <p className="text-[11px] text-teal-800">
              {lang === 'es'
                ? 'Restauramos su progreso. Continúe donde lo dejó.'
                : 'We restored your progress — pick up where you left off.'}
              {' '}
              <button onClick={() => { clearDraft(token); window.location.reload(); }}
                className="font-semibold underline hover:no-underline">
                {lang === 'es' ? 'Comenzar de nuevo' : 'Start over'}
              </button>
            </p>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        {/* Validation banner — fires only after attempted Next/Submit so
            the form doesn't shout at the patient on initial render.
            Lists every missing field by label + a Spanish fallback so
            front-desk staff can read the list aloud over the phone if
            needed. */}
        {attemptedAdvance && !validation.ok && (
          <div className="mb-3 rounded-xl border-2 border-rose-300 bg-rose-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-rose-200 text-rose-700">
                <span className="text-[16px] font-bold">!</span>
              </div>
              <div className="flex-1">
                <p className="text-[14px] font-bold text-rose-900">
                  {lang === 'es'
                    ? `Por favor complete ${validation.missingLabels.length} campo${validation.missingLabels.length === 1 ? '' : 's'}:`
                    : `Please complete ${validation.missingLabels.length} field${validation.missingLabels.length === 1 ? '' : 's'}:`}
                </p>
                <ul className="mt-2 list-disc pl-5 text-[13px] text-rose-800">
                  {validation.missingLabels.map((label, i) => (
                    <li key={i}>{label}</li>
                  ))}
                </ul>
                <p className="mt-2 text-[12px] italic text-rose-700">
                  {lang === 'es'
                    ? 'Su progreso se guarda automáticamente — no perderá esta información.'
                    : 'Your progress is auto-saved — you won\'t lose this information.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-900/[0.04]">
          {template.description && (
            <p className="mb-4 text-[14px] text-gray-600">{template.description}</p>
          )}

          <div className="space-y-5">
            {fields.filter((field) => !isCeremonyField(field, template) && isFieldVisible(field, responses, currentPatient?.gender)).map((field) => (
              <div
                key={field.id}
                data-field-id={field.id}
                id={`field-${field.id}`}
                className={
                  attemptedAdvance && validation.missingFieldIds.includes(field.id)
                    ? 'rounded-lg ring-2 ring-rose-300 ring-offset-2 transition-all'
                    : ''
                }
              >
                {LOCKED_IDENTITY_FIELDS.has(field.id) && identityValueFor(field.id, currentPatient) ? (
                  // Legal identity is NOT patient-editable on forms. Patients
                  // were replacing their legal first name with a nickname,
                  // which breaks insurance/billing matching (Michael, Nathan,
                  // Anna 2026-07-15). The "Name you go by" field is the
                  // patient-owned place for nicknames; spelling corrections
                  // go through the front desk against a photo ID.
                  <div>
                    <FieldLabel label={field.label} required={false} lang={lang} />
                    <div className="w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-[15px] text-gray-700">
                      {identityValueFor(field.id, currentPatient)}
                    </div>
                    <p className="mt-1 text-[11.5px] text-gray-400">
                      {lang === 'es'
                        ? 'Si su nombre legal está mal escrito, avise a la recepción para corregirlo.'
                        : 'If your legal name is misspelled, let the front desk know and we will correct it.'}
                    </p>
                  </div>
                ) : (
                  <IntakeField onPhotoBusy={photoBusy} field={field} value={responses[field.id]} onChange={(v) => updateField(field.id, v)} lang={lang} />
                )}
              </div>
            ))}

            {/* Signing happens ONCE on the Review & Sign step — a note
                here so patients don't hunt for a signature box. */}
            {template.requires_signature && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="rounded-xl bg-teal-50 px-4 py-3 text-[13px] text-teal-800">
                  {lang === 'es'
                    ? 'Firmará este formulario en el paso final de "Revisar y firmar".'
                    : 'You will sign this form on the final "Review & sign" step.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Family member indicator */}
        {isMultiPatient && (
          <div className="mt-4 flex items-center justify-center gap-2">
            {patients.map((pt, idx) => (
              <span key={pt.id} className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                idx === currentPatientIdx ? 'bg-teal-600 text-white' : idx < currentPatientIdx ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'
              }`}>{pt.first_name}</span>
            ))}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => {
              captureCurrentSignature();
              setTimeout(() => {
                if (currentForm > 0) { setCurrentForm(currentForm - 1); }
                else if (currentPatientIdx > 0) {
                  // Land on the LAST form of the previous patient's own
                  // list — family lists differ in length (age routing).
                  setCurrentPatientIdx(currentPatientIdx - 1);
                  setCurrentForm(Math.max(0, templatesFor(patients[currentPatientIdx - 1]).length - 1));
                }
              }, 50);
            }}
            disabled={preparingPhotos > 0 || (currentForm === 0 && currentPatientIdx === 0)}
            className="flex items-center gap-1.5 rounded-xl px-5 py-3 text-[14px] font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>

          {isLast ? (
            <button
              onClick={() => handleAttemptAdvance(() => {
                // Default the printed name to whoever is most likely
                // signing (the primary/adult patient) — editable on the
                // review screen.
                if (!reviewName) {
                  const adult = patients.find((p) => !patientIsMinor(p)) || patients[0];
                  if (adult) setReviewName(`${adult.first_name || ''} ${adult.last_name || ''}`.trim());
                }
                setReviewing(true);
                window.scrollTo({ top: 0 });
              })}
              disabled={submitting || preparingPhotos > 0}
              className="flex items-center gap-2 rounded-xl bg-teal-600 px-8 py-3 text-[14px] font-bold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-50"
            >
              {lang === 'es' ? 'Revisar y firmar' : 'Review & Sign'}
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button disabled={preparingPhotos > 0}
              onClick={() => handleAttemptAdvance(() => {
                captureCurrentSignature();
                setTimeout(() => {
                  if (currentForm < templates.length - 1) { setCurrentForm(currentForm + 1); }
                  else if (currentPatientIdx < patients.length - 1) {
                    setCurrentPatientIdx(currentPatientIdx + 1);
                    setCurrentForm(0);
                  }
                }, 50);
              })}
              className="flex items-center gap-1.5 rounded-xl bg-teal-600 px-6 py-3 text-[14px] font-bold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-50"
            >
              {isLastForm && !isLastPatient ? `Next: ${patients[currentPatientIdx + 1]?.first_name}'s Forms` : 'Next'}
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Intake Field Renderer — large, touch-friendly inputs
// ============================================================

// Identity fields locked on patient-facing forms — value comes from the
// chart; only staff may change legal name / DOB (verified against ID).
// Minor detection for signature attribution — guardians sign for
// minors and the record must say so (Michael Rivera 2026-07-16:
// mother signed, form said "signed by (self)").
function patientIsMinor(patient) {
  const dob = patient?.date_of_birth;
  if (!dob) return false;
  const d = new Date(String(dob).slice(0, 10) + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) < 18 * 365.25 * 24 * 3600 * 1000;
}
const SIGNER_RELATIONSHIPS = [
  { value: 'self', en: 'Myself (the patient)', es: 'Yo (el paciente)' },
  { value: 'parent', en: 'Parent', es: 'Padre/Madre' },
  { value: 'legal_guardian', en: 'Legal guardian', es: 'Tutor legal' },
  { value: 'spouse', en: 'Spouse', es: 'Cónyuge' },
  { value: 'power_of_attorney', en: 'Power of attorney', es: 'Poder notarial' },
];

const LOCKED_IDENTITY_FIELDS = new Set(['first_name', 'last_name', 'middle_name', 'middle_initial', 'dob', 'date_of_birth']);
function identityValueFor(fieldId, patient) {
  if (!patient) return '';
  if (fieldId === 'first_name') return patient.first_name || '';
  if (fieldId === 'last_name') return patient.last_name || '';
  if (fieldId === 'middle_name' || fieldId === 'middle_initial') return patient.middle_name || '';
  if (fieldId === 'dob' || fieldId === 'date_of_birth') return (patient.date_of_birth || '').slice(0, 10);
  return '';
}

function IntakeField({ field, value, onChange, onPhotoBusy, lang = 'en' }) {
  const label = t(field.label, lang);
  const options = field.options ? tOptions(field.options, lang) : [];
  const originalOptions = field.options || []; // keep originals for storing values in English

  switch (field.type) {
    case 'section':
      return <h2 className="text-[16px] font-bold text-gray-900 pt-3 border-t border-gray-100">{label}</h2>;

    case 'static_text':
      return (
        <div>
          <h3 className="text-[14px] font-semibold text-gray-900 mb-2">{label}</h3>
          <div className="rounded-xl bg-gray-50 p-4 text-[13px] text-gray-600 leading-relaxed whitespace-pre-line max-h-[200px] overflow-y-auto">
            {field.content}
          </div>
        </div>
      );

    case 'textarea':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3}
            placeholder={field.placeholder}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20" />
        </div>
      );

    case 'select':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <select value={value || ''} onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none">
            <option value="">{tUI('selectOption', lang)}</option>
            {originalOptions.map((o, i) => <option key={o} value={o}>{options[i] || o}</option>)}
          </select>
        </div>
      );

    case 'radio':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="flex flex-wrap gap-2">
            {originalOptions.map((o, i) => (
              <button key={o} type="button" onClick={() => onChange(o)}
                className={`rounded-xl border-2 px-5 py-2.5 text-[14px] font-medium transition-all ${
                  value === o ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}>
                {options[i] || o}
              </button>
            ))}
          </div>
        </div>
      );

    case 'checkbox':
      return (
        <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-teal-600" />
          <span className="text-[14px] text-gray-700 leading-relaxed">
            {label} {field.required && <span className="text-red-500">*</span>}
          </span>
        </label>
      );

    case 'checkbox_group':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="grid grid-cols-2 gap-2">
            {originalOptions.map((o, i) => {
              const selected = Array.isArray(value) && value.includes(o);
              return (
                <label key={o} className={`flex items-center gap-2.5 rounded-xl border-2 px-4 py-2.5 cursor-pointer transition-all ${
                  selected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input type="checkbox" checked={selected}
                    onChange={(e) => {
                      const arr = Array.isArray(value) ? [...value] : [];
                      if (e.target.checked) arr.push(o);
                      else arr.splice(arr.indexOf(o), 1);
                      onChange(arr);
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600" />
                  <span className="text-[13px] text-gray-700">{options[i] || o}</span>
                </label>
              );
            })}
          </div>
        </div>
      );

    case 'pain_scale':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="flex gap-1.5 justify-center">
            {Array.from({ length: 11 }).map((_, i) => (
              <button key={i} type="button" onClick={() => onChange(i)}
                className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 text-[15px] font-bold transition-all ${
                  value === i
                    ? i <= 1 ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : i <= 3 ? 'border-yellow-500 bg-yellow-50 text-yellow-700' : i <= 6 ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 text-gray-500'
                }`}>
                {i}
              </button>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[11px] text-gray-400 px-1">
            <span>No Pain</span>
            <span>Worst Pain</span>
          </div>
        </div>
      );

    case 'address':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="space-y-2">
            <input type="text" value={value?.street || ''} onChange={(e) => onChange({ ...(value || {}), street: e.target.value })}
              placeholder="Street Address"
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={value?.city || ''} onChange={(e) => onChange({ ...(value || {}), city: e.target.value })}
                placeholder="City"
                className="rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
              <input type="text" value={value?.state || ''} onChange={(e) => onChange({ ...(value || {}), state: e.target.value })}
                placeholder="State" maxLength={2}
                className="rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
              <input type="text" value={value?.zip || ''} onChange={(e) => onChange({ ...(value || {}), zip: e.target.value })}
                placeholder="ZIP"
                className="rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
            </div>
          </div>
        </div>
      );

    case 'photo':
      return <div><FieldLabel label={field.label} required={field.required} lang={lang} />
        <IntakePhoto key={field.id} onBusy={onPhotoBusy} value={value} onChange={onChange} label={field.label} lang={lang} />
      </div>;

    default: // text, email, phone, date
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <input
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'date' ? 'date' : 'text'}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
          />
        </div>
      );
  }
}

function FieldLabel({ label, required, lang }) {
  return (
    <label className="mb-1.5 block text-[14px] font-semibold text-gray-700">
      {t(label, lang)} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

// ============================================================
// Screen states
// ============================================================

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-teal-50 to-white">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
        <p className="mt-4 text-[14px] text-gray-500">Loading your forms...</p>
      </div>
    </div>
  );
}

// First screen the patient sees — pick a language before any form renders.
// Both options are always visible and large so a Spanish speaker can self-serve.
function LanguageGate({ practiceName, onPick }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-teal-50 to-white px-6 py-10">
      <div className="mb-6 flex items-center gap-2">
        <Stethoscope className="h-6 w-6 text-teal-700" />
        <h1 className="text-[20px] font-bold text-teal-900">{practiceName || 'Welcome'}</h1>
      </div>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-900/[0.06]">
        <div className="mb-5 flex items-center justify-center gap-2 text-gray-500">
          <Globe className="h-5 w-5" />
          <p className="text-center text-[14px] font-semibold leading-tight">
            Choose your language<br />
            <span className="text-teal-700">Elige tu idioma</span>
          </p>
        </div>
        <button
          onClick={() => onPick('en')}
          className="mb-3 flex min-h-[56px] w-full items-center justify-center rounded-xl bg-teal-700 px-4 text-[18px] font-bold text-white transition-transform hover:bg-teal-800 active:scale-[0.99]"
        >
          English
        </button>
        <button
          onClick={() => onPick('es')}
          className="flex min-h-[56px] w-full items-center justify-center rounded-xl border-2 border-teal-700 px-4 text-[18px] font-bold text-teal-800 transition-colors hover:bg-teal-50 active:scale-[0.99]"
        >
          Español
        </button>
      </div>
    </div>
  );
}

function ErrorScreen({ kind = 'load', message, onRetry }) {
  const header = kind === 'submit' ? 'We Could Not Save Your Forms' : 'Unable to Load Forms';
  const footer = kind === 'submit'
    ? 'Keep this page open and contact the office to check what was saved.'
    : 'If retrying does not help, contact the office to check this link and any saved progress.';
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-teal-50 to-white p-6">
      <div className="max-w-md text-center">
        <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-amber-500" />
        <h2 className="text-[18px] font-bold text-gray-900">{header}</h2>
        <p className="mt-2 text-[14px] text-gray-600">{message}</p>
        <p className="mt-4 text-[13px] text-gray-400">{footer}</p>
        {onRetry && <button type="button" onClick={onRetry} className="mt-4 rounded bg-teal-700 px-4 py-2 text-white">Try loading again</button>}
      </div>
    </div>
  );
}

function SuccessScreen({ practiceName }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-teal-50 to-white p-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-100">
          <CheckCircle className="h-8 w-8 text-teal-600" />
        </div>
        <h2 className="text-[22px] font-bold text-gray-900">All Done!</h2>
        <p className="mt-2 text-[15px] text-gray-600">
          Your forms have been submitted successfully. Thank you!
        </p>
        <p className="mt-4 text-[13px] text-gray-400">
          Please return this device to the front desk.
        </p>
        <div className="mt-8 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-900/[0.04]">
          <Stethoscope className="mx-auto mb-2 h-6 w-6 text-teal-600" />
          <p className="text-[14px] font-semibold text-gray-900">{practiceName}</p>
        </div>
      </div>
    </div>
  );
}
